'use client';

import { recordImpression, recordView } from '@/actions/public/server.actions';
import useUserProfile from '@/hooks/useUserProfile';
import React, { useEffect, useRef, useCallback, useState } from 'react';

function getPageQuery(pagePayload) {
  let query = {
    type: pagePayload.type,
  };
  switch (pagePayload.type) {
    case 'individual_public_profile_page':
      query.userId = pagePayload.userId;
      break;
    case 'nonprofit_public_page':
      query.nonProfitId = pagePayload.nonProfitId;
      break;
    case 'company_public_page':
      query.companyId = pagePayload.companyId;
      break;
    case 'detailed_post_page':
      query.postId = pagePayload.postId;
      if (pagePayload.nonProfitId) {
        query.nonProfitId = pagePayload.nonProfitId;
      } else if (pagePayload.companyId) {
        query.companyId = pagePayload.companyId;
      }
      break;
    case 'post':
      query.postId = pagePayload.postId;
      break;
    default:
      console.log('Invalid analytics page type');
  }
  return query;
}

const TrackPageLifecycle = ({ pagePayload, children }) => {
  const [isUserReady, setIsUserReady] = useState(false);
  const startTimeRef = useRef(null);
  const hasMountedRef = useRef(false);
  const handleUnmountRef = useRef(false);

  const user = useUserProfile();

  // Function to record impression when the component mounts
  const handleMount = useCallback(() => {
    console.log('handleMount called');
    const query = getPageQuery(pagePayload);
    const payload = {
      ...query,
      impression: {
        userId: user?.id,
        activeCommunityId: user?.activeCommunity?.id,
        location: user?.location,
      },
    };
    if (user.id > 0) {
      recordImpression(payload);
    }
  }, [pagePayload, user]);

  // Function to record view when the component unmounts
  const handleUnmount = useCallback(() => {
    console.log('handleUnmount called');
    if (startTimeRef.current) {
      const durationInSeconds = (Date.now() - startTimeRef.current) / 1000;
      const query = getPageQuery(pagePayload);
      const payload = {
        ...query,
        view: {
          userId: user?.id,
          activeCommunityId: user?.activeCommunity?.id,
          location: user?.location,
          duration: durationInSeconds.toFixed(2),
        },
      };
      if (user.id > 0) {
        recordView(payload);
      }
    }
  }, [pagePayload, user]);

  useEffect(() => {
    if (user) {
      setIsUserReady(true);
    }
  }, [user]);

  useEffect(() => {
    if (isUserReady) {
      if (!hasMountedRef.current) {
        hasMountedRef.current = true;
        startTimeRef.current = Date.now();

        // Call handleMount immediately when the component mounts
        handleMount();
      }

      // Setup beforeunload event to handle tab/browser close
      const handleBeforeUnload = () => {
        if (!handleUnmountRef.current) {
          handleUnmount();
          handleUnmountRef.current = true;
        }
      };

      window.addEventListener('beforeunload', handleBeforeUnload);

      // Cleanup logic
      return () => {
        window.removeEventListener('beforeunload', handleBeforeUnload);
        if (!handleUnmountRef.current) {
          handleUnmount();
          handleUnmountRef.current = true;
        }
      };
    }
  }, [isUserReady, handleMount, handleUnmount]);

  // Skip tracking if the user is not logged in
  if (!user || user.id <= 0) {
    return children;
  }

  return children;
};

export default TrackPageLifecycle;
